import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Switch } from '@/components/ui/switch';
import { usePlatformCostsApi } from '../../../common/hooks/use-platform-costs-api.hook';
import { COST_PROVIDERS, PROVIDER_NAMES, type CostProviderId, type CostSettings } from '../../../common/models/platform-costs.model';

type Money = string;
interface Draft {
  alertsEnabled: boolean;
  monthlyBudgetUsd: Money;
  providers: Record<CostProviderId, { lowBalanceUsd: Money; monthlyBudgetUsd: Money }>;
  credit: { amount: Money; currency: string; startsAt: string; expiresAt: string };
}

const str = (v: number | null | undefined) => (v == null ? '' : String(v));
const num = (v: Money) => (v.trim() === '' ? null : Math.max(Number(v) || 0, 0));

function toDraft(s: CostSettings, defaults: Partial<Record<CostProviderId, number | null>>): Draft {
  const providers = {} as Draft['providers'];
  for (const id of COST_PROVIDERS) {
    const p = s.providers?.[id] || {};
    // An unset threshold shows the default the API applies, so the field never lies.
    providers[id] = { lowBalanceUsd: p.lowBalanceUsd === undefined ? str(defaults[id]) : str(p.lowBalanceUsd), monthlyBudgetUsd: str(p.monthlyBudgetUsd) };
  }
  return {
    alertsEnabled: s.alertsEnabled,
    monthlyBudgetUsd: str(s.monthlyBudgetUsd),
    providers,
    credit: {
      amount: str(s.azureCredit?.amount),
      currency: s.azureCredit?.currency || 'USD',
      startsAt: s.azureCredit?.startsAt || '',
      expiresAt: s.azureCredit?.expiresAt || '',
    },
  };
}

/** Thresholds, budgets, whether to alert, and the Azure credit Azure itself won't report. */
export function CostSettingsDialog({
  open,
  onOpenChange,
  settings,
  thresholds,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: CostSettings;
  /** The thresholds in effect now (defaults included), per provider. */
  thresholds: Partial<Record<CostProviderId, number | null>>;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const api = usePlatformCostsApi();
  const [draft, setDraft] = useState<Draft>(() => toDraft(settings, thresholds));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft(toDraft(settings, thresholds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setProvider = (id: CostProviderId, field: 'lowBalanceUsd' | 'monthlyBudgetUsd', value: string) =>
    setDraft((d) => ({ ...d, providers: { ...d.providers, [id]: { ...d.providers[id], [field]: value } } }));
  const setCredit = (field: keyof Draft['credit'], value: string) => setDraft((d) => ({ ...d, credit: { ...d.credit, [field]: value } }));

  const creditValid = !draft.credit.amount || (num(draft.credit.amount) > 0 && /^\d{4}-\d{2}-\d{2}$/.test(draft.credit.startsAt));

  const save = async () => {
    setSaving(true);
    try {
      const providers = {} as CostSettings['providers'];
      for (const id of COST_PROVIDERS) providers[id] = { lowBalanceUsd: num(draft.providers[id].lowBalanceUsd), monthlyBudgetUsd: num(draft.providers[id].monthlyBudgetUsd) };
      await api.updateSettings({
        alertsEnabled: draft.alertsEnabled,
        monthlyBudgetUsd: num(draft.monthlyBudgetUsd),
        providers,
        azureCredit: num(draft.credit.amount)
          ? { amount: num(draft.credit.amount), currency: draft.credit.currency, startsAt: draft.credit.startsAt, expiresAt: draft.credit.expiresAt || null }
          : null,
      });
      toast.success(t('ADMIN_COSTS.SETTINGS_DIALOG.SAVED'));
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error(t('ADMIN_COSTS.SETTINGS_DIALOG.SAVE_FAILED'));
    } finally {
      setSaving(false);
    }
  };

  const money = (props: React.ComponentProps<typeof Input>) => <Input type="number" min={0} step="1" inputMode="decimal" className="h-8 tabular-nums" {...props} />;

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="costs-settings">
        <DialogHeader>
          <DialogTitle>{t('ADMIN_COSTS.SETTINGS_DIALOG.TITLE')}</DialogTitle>
          <DialogDescription>{t('ADMIN_COSTS.SETTINGS_DIALOG.DESCRIPTION')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="costs-alerts">{t('ADMIN_COSTS.SETTINGS_DIALOG.ALERTS')}</Label>
            <Switch id="costs-alerts" checked={draft.alertsEnabled} onCheckedChange={(v) => setDraft((d) => ({ ...d, alertsEnabled: v }))} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="costs-total-budget">{t('ADMIN_COSTS.SETTINGS_DIALOG.TOTAL_BUDGET')}</Label>
            {money({ id: 'costs-total-budget', value: draft.monthlyBudgetUsd, placeholder: t('ADMIN_COSTS.SETTINGS_DIALOG.NONE'), onChange: (e) => setDraft((d) => ({ ...d, monthlyBudgetUsd: e.target.value })), className: 'h-8 max-w-48 tabular-nums' })}
          </div>

          <div>
            <div className="grid grid-cols-[1fr_7rem_7rem] items-center gap-x-3 gap-y-2 text-xs">
              <span className="text-muted-foreground">{t('ADMIN_COSTS.SETTINGS_DIALOG.PROVIDER')}</span>
              <span className="text-muted-foreground">{t('ADMIN_COSTS.SETTINGS_DIALOG.LOW_BALANCE')}</span>
              <span className="text-muted-foreground">{t('ADMIN_COSTS.SETTINGS_DIALOG.BUDGET')}</span>
              {COST_PROVIDERS.map((id) => (
                <div key={id} className="contents">
                  <span className="text-sm">{PROVIDER_NAMES[id]}</span>
                  {money({ 'aria-label': `${PROVIDER_NAMES[id]} — ${t('ADMIN_COSTS.SETTINGS_DIALOG.LOW_BALANCE')}`, value: draft.providers[id].lowBalanceUsd, placeholder: t('ADMIN_COSTS.SETTINGS_DIALOG.NONE'), onChange: (e) => setProvider(id, 'lowBalanceUsd', e.target.value) })}
                  {money({ 'aria-label': `${PROVIDER_NAMES[id]} — ${t('ADMIN_COSTS.SETTINGS_DIALOG.BUDGET')}`, value: draft.providers[id].monthlyBudgetUsd, placeholder: t('ADMIN_COSTS.SETTINGS_DIALOG.NONE'), onChange: (e) => setProvider(id, 'monthlyBudgetUsd', e.target.value) })}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border p-4">
            <p className="text-sm font-medium">{t('ADMIN_COSTS.SETTINGS_DIALOG.AZURE_CREDIT')}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t('ADMIN_COSTS.SETTINGS_DIALOG.AZURE_CREDIT_HINT')}</p>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="costs-credit-amount" className="text-xs">{t('ADMIN_COSTS.SETTINGS_DIALOG.AMOUNT')}</Label>
                {money({ id: 'costs-credit-amount', value: draft.credit.amount, onChange: (e) => setCredit('amount', e.target.value) })}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="costs-credit-currency" className="text-xs">{t('ADMIN_COSTS.SETTINGS_DIALOG.CURRENCY')}</Label>
                <NativeSelect id="costs-credit-currency" size="sm" className="w-full" value={draft.credit.currency} onChange={(e) => setCredit('currency', e.target.value)}>
                  <NativeSelectOption value="USD">USD</NativeSelectOption>
                  <NativeSelectOption value="EUR">EUR</NativeSelectOption>
                </NativeSelect>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="costs-credit-start" className="text-xs">{t('ADMIN_COSTS.SETTINGS_DIALOG.STARTS')}</Label>
                <Input id="costs-credit-start" type="date" className="h-8" value={draft.credit.startsAt} onChange={(e) => setCredit('startsAt', e.target.value)} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="costs-credit-end" className="text-xs">{t('ADMIN_COSTS.SETTINGS_DIALOG.EXPIRES')}</Label>
                <Input id="costs-credit-end" type="date" className="h-8" value={draft.credit.expiresAt} onChange={(e) => setCredit('expiresAt', e.target.value)} />
              </div>
            </div>
            {draft.credit.amount && (
              <Button variant="link" size="sm" className="mt-2 h-auto px-0 text-xs" onClick={() => setDraft((d) => ({ ...d, credit: { amount: '', currency: 'USD', startsAt: '', expiresAt: '' } }))}>
                {t('ADMIN_COSTS.SETTINGS_DIALOG.CLEAR_CREDIT')}
              </Button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('ADMIN_COSTS.SETTINGS_DIALOG.CANCEL')}
          </Button>
          <Button variant="outline" onClick={save} disabled={saving || !creditValid} data-testid="costs-settings-save">
            {t('ADMIN_COSTS.SETTINGS_DIALOG.SAVE')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
